import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { aiGeneratorService, apiErrorMessage, CONTENT_TYPES, type ContentJob, type GeneratedContent, type GeneratedContentType } from '../../services/aiGeneratorService';
import type { Block } from '../Courses/editors/BlockEditor/types';
import { BlockInteractionProvider } from '../CoursePlayer/players/blocks/BlockInteractionContext';
import { BlockList } from '../CoursePlayer/players/blocks/BlockPlayer';
import { emptyMaterial, MaterialInput, type MaterialValue } from './MaterialInput';
import { emptyKnowledgeSelection, KnowledgeSourcePicker, type KnowledgeSelection } from './KnowledgeSourcePicker';
import { DIFFICULTIES, LANGUAGES } from './options';

interface CourseOption {
    id: number;
    title: string;
    contentFormat?: 'classic' | 'blocks';
}

interface SectionOption {
    id: number;
    title: string;
}

const MIN_CHARS = 50;

/* ───────────────────────── form ───────────────────────── */

export const ContentJobForm: React.FC<{ onCreated: (job: ContentJob) => void; disabled?: boolean }> = ({ onCreated, disabled }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [material, setMaterial] = useState<MaterialValue>(emptyMaterial());
    const [types, setTypes] = useState<GeneratedContentType[]>(['lesson_draft']);
    const [language, setLanguage] = useState('de');
    const [difficulty, setDifficulty] = useState('');
    const [knowledge, setKnowledge] = useState<KnowledgeSelection>(emptyKnowledgeSelection());
    const [submitting, setSubmitting] = useState(false);

    const toggleType = (type: GeneratedContentType) => setTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
    const chars = material.text.trim().length;
    // #514: with the knowledge base in use the material is optional — empty means
    // "generate from the selected sources", a short text is a focus to search them for.
    const canSubmit = !disabled && !submitting && types.length > 0 && (chars >= MIN_CHARS || knowledge.use || (material.mode === 'url' && material.url.trim() !== '' && chars === 0));

    const submit = async () => {
        setSubmitting(true);
        try {
            const useUrlDirectly = material.mode === 'url' && chars === 0;
            const job = await aiGeneratorService.createContentJob({
                source_type: useUrlDirectly ? 'url' : material.sourceType === 'url' || !material.sourceType ? 'plain_text' : (material.sourceType as 'pdf' | 'docx' | 'pptx'),
                source_text: useUrlDirectly ? undefined : material.text.trim(),
                source_url: useUrlDirectly ? material.url.trim() : undefined,
                target_types: types,
                language,
                difficulty_level: difficulty || null,
                use_knowledge_base: knowledge.use,
                knowledge_source_ids: knowledge.use && knowledge.sourceIds.length > 0 ? knowledge.sourceIds : null,
            });
            notify(translate('aiGenerator.content.created'), { type: 'success' });
            onCreated(job);
        } catch (e) {
            notify(apiErrorMessage(e, translate('aiGenerator.content.createError')), { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="oa-aig-card">
            <h2 className="oa-aig-card-title">
                <span className="oa-aig-icon"><AutoAwesomeOutlined fontSize="small" /></span>
                {translate('aiGenerator.content.title')}
            </h2>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{translate('aiGenerator.content.intro')}</Typography>
            <MaterialInput value={material} onChange={setMaterial} minChars={MIN_CHARS} optional={knowledge.use} disabled={disabled || submitting} />
            <KnowledgeSourcePicker value={knowledge} onChange={setKnowledge} disabled={disabled || submitting} />
            <Typography variant="subtitle2" sx={{ mt: 2 }}>{translate('aiGenerator.content.types')}</Typography>
            <div className="oa-aig-types">
                {CONTENT_TYPES.map((type) => (
                    <FormControlLabel
                        key={type}
                        control={<Checkbox size="small" checked={types.includes(type)} onChange={() => toggleType(type)} disabled={disabled || submitting} />}
                        label={translate(`aiGenerator.types.${type}`)}
                    />
                ))}
            </div>
            <div className="oa-aig-form-row">
                <TextField select size="small" label={translate('aiGenerator.fields.language')} value={language} onChange={(e) => setLanguage(e.target.value)} disabled={disabled || submitting}>
                    {LANGUAGES.map((l) => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
                </TextField>
                <TextField select size="small" label={translate('aiGenerator.fields.difficulty')} value={difficulty} onChange={(e) => setDifficulty(e.target.value)} disabled={disabled || submitting}>
                    <MenuItem value="">{translate('aiGenerator.fields.difficultyAny')}</MenuItem>
                    {DIFFICULTIES.map((d) => <MenuItem key={d} value={d}>{translate(`aiGenerator.difficulty.${d}`)}</MenuItem>)}
                </TextField>
            </div>
            <div className="oa-aig-actions">
                <Button variant="contained" onClick={() => void submit()} disabled={!canSubmit} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeOutlined />}>
                    {translate('aiGenerator.content.submit')}
                </Button>
                {types.length === 0 && <Typography variant="caption" color="error">{translate('aiGenerator.content.noTypes')}</Typography>}
            </div>
        </div>
    );
};

/* ───────────────────────── results ───────────────────────── */

const FreeTextPreview: React.FC<{ content: Record<string, unknown> }> = ({ content }) => {
    const translate = useTranslate();
    const questions = Array.isArray(content.questions) ? (content.questions as { text?: string; explanation?: string; answers?: { text?: string }[] }[]) : [];
    return (
        <div>
            {questions.map((q, i) => (
                <div className="oa-aig-freetext-q" key={i}>
                    <strong>{q.text}</strong>
                    {q.answers?.[0]?.text && <div>{translate('aiGenerator.results.sampleAnswer')}: {q.answers[0].text}</div>}
                    {q.explanation && <div className="oa-aig-material-meta">{q.explanation}</div>}
                </div>
            ))}
        </div>
    );
};

const ResultPreview: React.FC<{ job: ContentJob; item: GeneratedContent }> = ({ job, item }) => {
    const translate = useTranslate();
    const [blocks, setBlocks] = useState<Block[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        if (item.type === 'free_text_question') {
            setBlocks([]);
            return () => { cancelled = true; };
        }
        aiGeneratorService.previewBlocks(job.id, item.id)
            .then((r) => { if (!cancelled) setBlocks(r.blocks); })
            .catch((e) => { if (!cancelled) setError(apiErrorMessage(e, translate('aiGenerator.results.previewError'))); });
        return () => { cancelled = true; };
    }, [job.id, item.id, item.type, translate]);

    if (error) return <Typography color="error" variant="body2">{error}</Typography>;
    if (blocks === null) return <CircularProgress size={20} />;
    if (item.type === 'free_text_question') return <FreeTextPreview content={item.content} />;
    return (
        <BlockInteractionProvider lessonId={null} readOnly>
            <BlockList blocks={blocks} />
        </BlockInteractionProvider>
    );
};

const AcceptDialog: React.FC<{ job: ContentJob; item: GeneratedContent | null; onClose: () => void; onAccepted: () => void }> = ({ job, item, onClose, onAccepted }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const navigate = useNavigate();
    const dataProvider = useDataProvider();
    const [courses, setCourses] = useState<CourseOption[]>([]);
    const [sections, setSections] = useState<SectionOption[]>([]);
    const [courseId, setCourseId] = useState<number | ''>(job.course_context_id ?? '');
    const [sectionId, setSectionId] = useState<number | ''>('');
    const [lessons, setLessons] = useState<{ id: number; title: string; type?: string }[]>([]);
    // #526: '' = new lesson; otherwise the blocks are appended to this existing block lesson.
    const [lessonId, setLessonId] = useState<number | ''>('');
    const [busy, setBusy] = useState(false);
    const appendable = item !== null && item.type !== 'free_text_question';

    useEffect(() => {
        if (!item) return;
        dataProvider.getList('courses', { pagination: { page: 1, perPage: 200 }, sort: { field: 'title', order: 'ASC' }, filter: {} })
            .then(({ data }) => setCourses(data as CourseOption[]))
            .catch(() => setCourses([]));
    }, [dataProvider, item]);

    const course = useMemo(() => courses.find((c) => c.id === courseId) ?? null, [courses, courseId]);

    useEffect(() => {
        setSectionId('');
        setLessonId('');
        if (!course || course.contentFormat !== 'blocks') { setSections([]); setLessons([]); return; }
        dataProvider.getList('lessons', { pagination: { page: 1, perPage: 500 }, sort: { field: 'sortOrder', order: 'ASC' }, filter: { 'course.id': course.id } })
            .then(({ data }) => setLessons((data as { id: number; title: string; type?: string }[]).filter((l) => !l.type || l.type === 'blocks')))
            .catch(() => setLessons([]));
        dataProvider.getList('course_sections', { pagination: { page: 1, perPage: 200 }, sort: { field: 'position', order: 'ASC' }, filter: { 'course.id': course.id } })
            .then(({ data }) => setSections(data as SectionOption[]))
            .catch(() => setSections([]));
    }, [dataProvider, course]);

    const accept = async () => {
        if (!item || courseId === '') return;
        setBusy(true);
        try {
            const result = await aiGeneratorService.acceptContent(job.id, item.id, { course_id: courseId, ...(lessonId !== '' ? { lesson_id: lessonId } : sectionId !== '' ? { section_id: sectionId } : {}) });
            notify(translate('aiGenerator.results.accepted', { lesson: result.lesson.title, course: result.course.title }), { type: 'success' });
            onAccepted();
            navigate(result.course.contentFormat === 'blocks' ? `/courses/${result.course.id}/builder/${result.lesson.id}` : `/lesson/${result.lesson.id}/content`);
        } catch (e) {
            notify(apiErrorMessage(e, translate('aiGenerator.results.acceptError')), { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={item !== null} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{translate('aiGenerator.results.acceptTitle')}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{translate('aiGenerator.results.acceptHint')}</Typography>
                <TextField select fullWidth size="small" sx={{ mb: 2 }} label={translate('aiGenerator.results.targetCourse')} value={courseId} onChange={(e) => setCourseId(e.target.value === '' ? '' : Number(e.target.value))} inputProps={{ 'aria-label': translate('aiGenerator.results.targetCourse') }}>
                    {courses.map((c) => <MenuItem key={c.id} value={c.id}>{c.title}{c.contentFormat === 'blocks' ? ' · v2' : ''}</MenuItem>)}
                </TextField>
                {course?.contentFormat === 'blocks' && appendable && lessons.length > 0 && (
                    <TextField select fullWidth size="small" sx={{ mb: 2 }} label={translate('aiGenerator.results.targetLesson')} value={lessonId} onChange={(e) => setLessonId(e.target.value === '' ? '' : Number(e.target.value))} inputProps={{ 'aria-label': translate('aiGenerator.results.targetLesson') }}>
                        <MenuItem value="">{translate('aiGenerator.results.newLesson')}</MenuItem>
                        {lessons.map((l) => <MenuItem key={l.id} value={l.id}>{l.title}</MenuItem>)}
                    </TextField>
                )}
                {course?.contentFormat === 'blocks' && lessonId === '' && (
                    <TextField select fullWidth size="small" label={translate('aiGenerator.results.targetSection')} value={sectionId} onChange={(e) => setSectionId(e.target.value === '' ? '' : Number(e.target.value))}>
                        <MenuItem value="">{translate('aiGenerator.results.draftSection')}</MenuItem>
                        {sections.map((s) => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
                    </TextField>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>{translate('ra.action.cancel')}</Button>
                <Button variant="contained" onClick={() => void accept()} disabled={busy || courseId === ''} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutline />}>
                    {translate('aiGenerator.results.accept')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const ContentJobResults: React.FC<{ job: ContentJob; onChanged: () => void }> = ({ job, onChanged }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const navigate = useNavigate();
    const [accepting, setAccepting] = useState<GeneratedContent | null>(null);
    const [expanded, setExpanded] = useState<number | null>(null);

    const reject = useCallback(async (item: GeneratedContent) => {
        try {
            await aiGeneratorService.rejectContent(job.id, item.id);
            notify(translate('aiGenerator.results.rejected'), { type: 'info' });
            onChanged();
        } catch (e) {
            notify(apiErrorMessage(e, translate('aiGenerator.results.rejectError')), { type: 'error' });
        }
    }, [job.id, notify, onChanged, translate]);

    const contents = job.contents ?? [];

    return (
        <div className="oa-aig-card">
            <h2 className="oa-aig-card-title">
                <span className="oa-aig-icon"><CheckCircleOutline fontSize="small" /></span>
                {translate('aiGenerator.results.title', { id: job.id })}
            </h2>
            {job.status === 'failed' && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{job.error_message ?? translate('aiGenerator.jobs.failed')}</Typography>}
            {job.status === 'completed' && job.error_message && <Typography color="warning.main" variant="body2" sx={{ mb: 1 }} role="status">{job.error_message}</Typography>}
            {job.source_data && <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1, whiteSpace: 'pre-wrap', maxHeight: 96, overflow: 'auto' }} data-testid="job-source">{translate('aiGenerator.results.source')}: {job.source_data}</Typography>}
            {job.status !== 'failed' && job.status !== 'completed' && (
                <div className="oa-aig-job-progress"><CircularProgress size={16} /> {translate(`aiGenerator.status.${job.status}`)}</div>
            )}
            {contents.length === 0 && job.status === 'completed' && <div className="oa-aig-empty">{translate('aiGenerator.results.empty')}</div>}
            <div className="oa-aig-results">
                {contents.map((item) => (
                    <div className="oa-aig-result" key={item.id}>
                        <div className="oa-aig-result-head">
                            <span className="oa-aig-result-title">{translate(`aiGenerator.types.${item.type}`)}</span>
                            <Chip size="small" label={translate(`aiGenerator.contentStatus.${item.status}`)} />
                            {item.status === 'pending' && (
                                <>
                                    <Button size="small" onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
                                        {expanded === item.id ? translate('aiGenerator.results.hidePreview') : translate('aiGenerator.results.preview')}
                                    </Button>
                                    <Button size="small" variant="contained" startIcon={<CheckCircleOutline />} onClick={() => setAccepting(item)}>{translate('aiGenerator.results.accept')}</Button>
                                    <Button size="small" color="inherit" startIcon={<DeleteOutline />} onClick={() => void reject(item)}>{translate('aiGenerator.results.reject')}</Button>
                                </>
                            )}
                            {item.status === 'accepted' && item.imported_lesson_id && (
                                <Button size="small" startIcon={<OpenInNewOutlined />} onClick={() => navigate(`/lesson/${item.imported_lesson_id}/content`)}>{translate('aiGenerator.results.openLesson')}</Button>
                            )}
                        </div>
                        {expanded === item.id && (
                            <div className="oa-aig-preview">
                                <ResultPreview job={job} item={item} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <AcceptDialog job={job} item={accepting} onClose={() => setAccepting(null)} onAccepted={() => { setAccepting(null); onChanged(); }} />
        </div>
    );
};
